import React, { createContext, useContext, useCallback } from 'react';
import { useTagStore } from '../store/useTagStore';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import tagRepository from '@/features/tags/repositories/TagRepository';
import pageRepository from '@/features/pages/repositories/PageRepository';

// Define the context type with all the methods
type TagsContextType = {
  refreshTags: () => Promise<void>;
  assignTag: (tag: string) => Promise<void>;
  unassignTag: (tag: string) => Promise<void>;
  updateTag: (oldTag: string, newTag: string) => Promise<void>;
  deleteTag: (tag: string) => Promise<void>;
  toggleFilter: (tag: string) => void;
  clearFilters: () => void;
};

const TagsContext = createContext<TagsContextType | null>(null);

export const TagsProvider = ({ children }: { children: React.ReactNode }) => {
  // Get state from stores using selectors
  const setTags = useTagStore(state => state.setTags);
  const toggleFilter = useTagStore(state => state.toggleFilter);
  const clearFilters = useTagStore(state => state.clearFilters);
  const currentPage = usePageStore(state => state.currentPage);
  const setCurrentPage = usePageStore(state => state.setCurrentPage);

  // Refresh tags
  const refreshTags = useCallback(async () => {
    const fetchedTags = await tagRepository.getTags();
    setTags(fetchedTags);
  }, [setTags]);

  // Assign a tag to the current page
  const assignTag = useCallback(
    async (tag: string) => {
      if (!currentPage?.id) return;

      await tagRepository.assignTag(currentPage.id, tag);
      await refreshTags();

      // Refresh current page to reflect tag changes
      const updatedPage = await pageRepository.getPageById(currentPage.id);
      if (updatedPage) {
        setCurrentPage(updatedPage);
      }
    },
    [currentPage, refreshTags, setCurrentPage]
  );

  // Unassign a tag from the current page
  const unassignTag = useCallback(
    async (tag: string) => {
      if (!currentPage?.id) return;

      await tagRepository.unassignTag(currentPage.id, tag);
      await refreshTags();

      // Refresh current page to reflect tag changes
      const updatedPage = await pageRepository.getPageById(currentPage.id);
      if (updatedPage) {
        setCurrentPage(updatedPage);
      }
    },
    [currentPage, refreshTags, setCurrentPage]
  );

  // Update/rename a tag
  const updateTag = useCallback(
    async (oldTag: string, newTag: string) => {
      await tagRepository.editTag(oldTag, newTag);
      await refreshTags();

      // Refresh current page if it has the tag
      if (currentPage?.tags?.includes(oldTag)) {
        const updatedPage = await pageRepository.getPageById(currentPage.id);
        if (updatedPage) {
          setCurrentPage(updatedPage);
        }
      }
    },
    [currentPage, refreshTags, setCurrentPage]
  );

  // Delete a tag
  const deleteTag = useCallback(
    async (tag: string) => {
      await tagRepository.deleteTag(tag);
      await refreshTags();

      // Refresh current page if it has the tag
      if (currentPage?.tags?.includes(tag)) {
        const updatedPage = await pageRepository.getPageById(currentPage.id);
        if (updatedPage) {
          setCurrentPage(updatedPage);
        }
      }
    },
    [currentPage, refreshTags, setCurrentPage]
  );

  const value = {
    refreshTags,
    assignTag,
    unassignTag,
    updateTag,
    deleteTag,
    toggleFilter,
    clearFilters,
  };

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
};

// Custom hook to use the context
export const useTags = () => {
  const context = useContext(TagsContext);
  if (!context) {
    throw new Error('useTags must be used within a TagsProvider');
  }
  return context;
};
